import { forwardRef } from 'react';
import { styled } from 'goober';
import { FlexRow } from '@components/layout/Flex';

export const DashboardTiles = styled(
    'div',
    forwardRef
)<{ $empty?: boolean; $scrollable?: boolean }>(({ theme, $empty, $scrollable }) => ({
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    overflow: $scrollable ? 'auto' : 'hidden',
    contain: 'paint',
    backgroundColor: theme.colors.ui.white,
    justifyContent: $empty ? 'center' : 'start',
    position: 'relative',
    borderTop: `1px solid ${theme?.colors.neutral.w200}`,
    borderBottom: `1px solid ${theme?.colors.neutral.w200}`,
}));

export const DashboardTilesFilter = styled(
    'div',
    forwardRef
)(() => ({
    padding: '0px 16px 15px',
}));

export const DashboardTilesFooter = styled(
    'div',
    forwardRef
)(() => ({
    padding: '0px 16px',
}));

export const DashboardTileCard = styled(
    'div',
    forwardRef
)<{
    $editing?: boolean;
    $dimmed?: boolean;
    $style?: {
        hasShadow?: boolean;
        showBackground?: boolean;
        showBorder?: boolean;
        backgroundColor?: string;
        borderColor?: string;
    };
}>(({ theme, $editing, $dimmed, $style }) => {
    return {
        position: 'absolute',
        left: 0,
        top: 0,
        width: '100%',
        height: '100%',
        background: $style?.showBackground ? $style?.backgroundColor : 'none',
        borderRadius: '8px',
        boxShadow: $style?.hasShadow ? `0px 4px 16px ${theme.boxShadow}` : 'none',
        transition:
            'left .5s ease-in-out, top .5s ease-in-out, width .5s ease-in-out, height .5s ease-in-out, opacity .15s ease-in-out',
        opacity: $dimmed ? '0.3' : '1',
        outline: $editing ? `1px solid ${theme.colors.primary.w500}` : 'none',

        padding: '8px',
        display: 'flex',
        flexDirection: 'column',
    };
});

export const DashboardTileTitle = styled('div')(({ theme }) => ({
    display: 'flex',
    flexShrink: 0,
    width: '100%',
    paddingBottom: '10px',
    fontSize: '12px',
    color: theme.colors.ui.dark,
}));

export const DashboardTileSubtitle = styled('div')(({ theme }) => ({
    width: '100%',
    fontSize: '12px',
    color: theme.colors.ui.dark,
    flexShrink: 0,
    paddingBottom: '10px',
}));

export const DashboardTileFootnotes = styled('div')(({ theme }) => ({
    width: '100%',
    fontSize: '11px',
    color: theme.colors.neutral.w700,
    flexShrink: 0,
    paddingTop: '8px',
}));

export const DashboardTileFilters = styled(FlexRow)<{ $reserve?: number }>(({ $reserve }) => ({
    position: 'relative',
    height: '28px',
    marginRight: `${$reserve}px`,
    flexShrink: 0,
}));

export const ActionButtonsContainer = styled('div')({
    position: 'absolute',
    top: '8px',
    right: '8px',
    display: 'flex',
    flexDirection: 'row',
    gap: '4px',
    alignItems: 'center',
    zIndex: 3,
});

export const DashboardTileContent = styled(
    'div',
    forwardRef
)<{ $isolate?: boolean }>(({ $isolate }) => ({
    // create new stacking context for position fixed and absolute
    position: 'relative',
    overflow: $isolate ? undefined : 'auto',
    contain: $isolate ? 'layout' : undefined,
    zIndex: $isolate ? 2 : 0,

    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
}));

export const DashboardTextContent = styled(
    DashboardTileContent,
    forwardRef
)(({ theme }) => ({
    '& figure': {
        margin: 0,
    },
    '& figure img': {
        display: 'block',
        maxWidth: '100%',
    },
    '& a': {
        color: theme.colors.primary.w500,
        textDecoration: 'none',
    },
    '& a:hover': {
        textDecoration: 'underline',
    },
}));
